'use client';

interface IdleTimerOptions {
  timeoutMs: number;
  onIdle: () => void;
  onActiveAfterIdle: () => void;
}

const INTERACTION_EVENTS = Object.freeze([
  'scroll',
  'keydown',
  'touchmove',
  'mousemove',
]);

const IDLE_TIMESTAMP_KEY = 'idleTimestamp';
const ONE_SECOND_MS = 1000;

export default class IdleTimer {
  private timeoutMs: number;
  private timerId: NodeJS.Timeout | null = null;
  private interactionEventHandler: () => void;

  constructor({ timeoutMs, onIdle, onActiveAfterIdle }: IdleTimerOptions) {
    this.timeoutMs = timeoutMs;
    this.interactionEventHandler = () => {
      if (this.isIdle()) {
        onActiveAfterIdle();
        this.initInterval(onIdle);
      } else {
        this.updateIdleTimestamp();
      }
    };

    if (this.isIdle()) {
      onIdle();
    }

    INTERACTION_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, this.interactionEventHandler),
    );

    this.initInterval(onIdle);
  }

  private initInterval(onIdle: () => void): void {
    this.updateIdleTimestamp();

    this.timerId = setInterval(() => {
      if (this.isIdle()) {
        onIdle();
        clearInterval(this.timerId!);
      }
    }, ONE_SECOND_MS);
  }

  cleanUp(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
    }

    INTERACTION_EVENTS.forEach((eventName) =>
      window.removeEventListener(eventName, this.interactionEventHandler),
    );
  }

  private isIdle(): boolean {
    const serializedIdleTime = localStorage.getItem(IDLE_TIMESTAMP_KEY);

    if (serializedIdleTime === null) {
      return false;
    }

    const idleTime = parseInt(serializedIdleTime, 10);

    return idleTime < Date.now();
  }

  private updateIdleTimestamp(): void {
    localStorage.setItem(
      IDLE_TIMESTAMP_KEY,
      (Date.now() + this.timeoutMs).toString(),
    );
  }
}
